import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  SwipeableDrawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useAuth } from '../AuthContext';
import { storage } from '../firebase';
import { buildPdfPayloadFromTemplate } from '../office/pdfPayloadBuilder';
import { compareUnitNumbers } from '../utils/unitSort';
import {
  buildChangeOrderNo,
  CHANGE_ORDER_CUSTOMER_APPROVAL_OPTIONS,
  CHANGE_ORDER_STATUS_OPTIONS,
  getDefaultAgencyForCategory,
  isOpenChangeOrderStatus,
  type ChangeOrderCustomerApproval,
  type ChangeOrderPriority,
  type ChangeOrderStatus,
  changeChangeOrderStatus,
} from '../office/changeOrders';

type UnitOption = {
  id: string;
  unitNumber: string;
  unitNumberLower: string;
  bookingStatus?: string | null;
  activeBookingId?: string | null;
  bookingFormDate?: string | null;
  latestBookingFormDate?: string | null;
  primaryCustomerName?: string | null;
  primaryMobile?: string | null;
  customerName?: string | null;
  allotteeName?: string | null;
};

type UnitPrimaryCustomerInfo = {
  name: string;
  mobile: string;
};

type ChangeOrderAttachment = {
  id: string;
  name: string;
  url: string;
  storagePath?: string;
  uploadedAt?: any;
  uploadedBy?: string;
};

type ChangeOrderDoc = {
  id: string;
  coNo: string;
  unitId: string;
  unitNo: string;
  customerName: string;
  title: string;
  description: string;
  category: string;
  agency: string;
  priority: ChangeOrderPriority;
  targetStage: string;
  dueDate?: any;
  status: ChangeOrderStatus;
  assignedTo: string;
  costImpact: boolean;
  customerApproval: ChangeOrderCustomerApproval;
  attachments: ChangeOrderAttachment[];
  createdAt?: any;
  createdBy?: string;
  updatedAt?: any;
  updatedBy?: string;
  statusUpdatedAt?: any;
};

type TimelineEvent = {
  id: string;
  type: 'created' | 'status_change' | 'comment' | 'updated' | 'attachment';
  message: string;
  fromStatus?: string;
  toStatus?: string;
  createdAt?: any;
  createdBy?: string;
};

type ChangeOrderFormState = {
  unitId: string;
  title: string;
  description: string;
  category: string;
  agencies: string[];
  targetStage: string;
  costImpact: boolean;
  customerApproval: ChangeOrderCustomerApproval;
  status: ChangeOrderStatus;
};

type SummaryFilter = '' | 'open' | 'pending_approval' | 'in_progress' | 'overdue' | 'completed_this_month';
type ChangeOrderSortKey = 'coNo' | 'unitNo' | 'customerName' | 'agency' | 'title' | 'status' | 'updatedAt';
type ChangeOrderViewMode = 'agency' | 'unit' | 'matrix';
type ChangeOrderExportOptions = {
  search: string;
  building: string;
  agency: string;
  targetStage: string;
  summaryFilter: SummaryFilter;
  viewMode: ChangeOrderViewMode;
  showContact: boolean;
  hideCompleted: boolean;
};
type MobilePickerKind =
  | 'unit'
  | 'title'
  | 'category'
  | 'agency'
  | 'targetStage'
  | 'customerApproval'
  | 'status'
  | 'costImpact';

type MobilePickerOption = {
  value: string;
  label: string;
};

const DEFAULT_CHANGE_ORDER_REQUEST_TITLES = [
  'Shift WC Position',
  'Change WC Style',
  'Change Kitchen Platform',
];

const DEFAULT_CHANGE_ORDER_CATEGORIES = [
  'WC Change',
  'Granite Change',
  'Groove In Flooring',
  'Tile Change',
];

const EMPTY_FORM: ChangeOrderFormState = {
  unitId: '',
  title: '',
  description: '',
  category: '',
  agencies: [],
  targetStage: '',
  costImpact: false,
  customerApproval: 'pending',
  status: 'requested',
};

const DEFAULT_EXPORT_OPTIONS: ChangeOrderExportOptions = {
  search: '',
  building: '',
  agency: '',
  targetStage: '',
  summaryFilter: '',
  viewMode: 'matrix',
  showContact: true,
  hideCompleted: true,
};

function asYmd(value: any): string {
  if (!value) return '';
  if (typeof value === 'string') {
    const text = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
    const date = new Date(text);
    if (!Number.isNaN(date.getTime())) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return '';
  }
  const date = typeof value?.toDate === 'function' ? value.toDate() : null;
  if (date && !Number.isNaN(date.getTime())) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
}

function formatDateTime(value: any): string {
  const date = typeof value?.toDate === 'function' ? value.toDate() : value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toMillis(value: any): number {
  const date = typeof value?.toDate === 'function' ? value.toDate() : value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
}

function statusLabel(value: string): string {
  const normalized = normalizeChangeOrderStatus(value);
  const found = CHANGE_ORDER_STATUS_OPTIONS.find((item) => item.value === normalized);
  return found?.label || value || '—';
}

function summaryFilterLabel(value: SummaryFilter): string {
  if (value === 'open') return 'Open';
  if (value === 'pending_approval') return 'Pending Approval';
  if (value === 'in_progress') return 'In Progress';
  if (value === 'overdue') return 'Overdue';
  if (value === 'completed_this_month') return 'Completed This Month';
  return 'All';
}

function normalizeChangeOrderStatus(value: unknown): ChangeOrderStatus {
  const raw = String(value || 'requested')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (raw === 'inprogress') return 'in_progress';
  if (raw === 'customerconfirmed') return 'customer_confirmed';
  if (raw === 'onhold') return 'on_hold';
  if (
    raw === 'requested' ||
    raw === 'reviewed' ||
    raw === 'approved' ||
    raw === 'assigned' ||
    raw === 'in_progress' ||
    raw === 'done' ||
    raw === 'customer_confirmed' ||
    raw === 'closed' ||
    raw === 'rejected' ||
    raw === 'on_hold'
  ) {
    return raw as ChangeOrderStatus;
  }
  return 'requested';
}

function isCompletedChangeOrderStatus(value: unknown): boolean {
  const normalized = normalizeChangeOrderStatus(value);
  return normalized === 'done' || normalized === 'closed' || normalized === 'customer_confirmed';
}

function normalizeChangeOrderCustomerApproval(value: unknown): ChangeOrderCustomerApproval {
  const raw = String(value || 'pending')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (raw === 'approved' || raw === 'rejected') return raw;
  return 'pending';
}

function priorityColor(priority: ChangeOrderPriority): 'default' | 'warning' | 'error' {
  if (priority === 'urgent') return 'error';
  if (priority === 'high') return 'warning';
  return 'default';
}

function formatPhoneReadable(value: unknown): string {
  const compact = String(value || '').trim().replace(/\s+/g, '');
  if (!compact) return '';
  if (compact.length <= 5) return compact;
  return `${compact.slice(0, 5)} ${compact.slice(5)}`;
}

const escHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const blobFromRenderTemplateResult = (resultData: unknown): Blob => {
  const data = (resultData || {}) as Record<string, any>;
  const mimeType = String(data.mimeType || 'application/pdf');
  const rawBytes = Array.isArray(data.pdfBytes)
    ? data.pdfBytes.map((item: any) => Number(item || 0))
    : data.pdfBytes && typeof data.pdfBytes === 'object'
      ? Object.keys(data.pdfBytes)
          .filter((key) => /^\d+$/.test(key))
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => Number((data.pdfBytes as Record<string, number>)[key] ?? 0))
      : null;
  if (rawBytes && rawBytes.length > 0) {
    return new Blob([new Uint8Array(rawBytes)], { type: mimeType });
  }
  const rawBase64 =
    typeof data.pdfBase64 === 'string'
      ? data.pdfBase64
      : data.pdfBase64?.type === 'Buffer' && Array.isArray(data.pdfBase64?.data)
        ? (() => {
            const source = data.pdfBase64?.data || [];
            let binaryBuffer = '';
            const chunkSize = 0x8000;
            for (let i = 0; i < source.length; i += chunkSize) {
              const chunk = source.slice(i, i + chunkSize);
              binaryBuffer += String.fromCharCode(...chunk);
            }
            return btoa(binaryBuffer);
          })()
        : '';
  if (!rawBase64) throw new Error('PDF response was empty.');
  const normalizedBase64 = String(rawBase64)
    .replace(/^data:application\/pdf;base64,/i, '')
    .replace(/\s+/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const paddedBase64 = normalizedBase64.padEnd(
    normalizedBase64.length + ((4 - (normalizedBase64.length % 4)) % 4),
    '='
  );
  const binary = atob(paddedBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
};

export default function OfficeChangeOrdersPage() {
  const { currentProjectId, currentUser, roleId, userRole } = useAuth();
  const db = useMemo(() => getFirestore(), []);
  const isAdmin = roleId === 'super-admin' || userRole === 'super-admin';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [units, setUnits] = useState<UnitOption[]>([]);
  const [changeOrders, setChangeOrders] = useState<ChangeOrderDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportPdfBusy, setExportPdfBusy] = useState(false);

  const [viewMode, setViewMode] = useState<ChangeOrderViewMode>('matrix');
  const [search, setSearch] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('');
  const [buildingFilter, setBuildingFilter] = useState('');
  const [targetStageFilter, setTargetStageFilter] = useState('');
  const [summaryFilter, setSummaryFilter] = useState<SummaryFilter>('');
  const [hideCompleted, setHideCompleted] = useState(true);
  const [sortBy, setSortBy] = useState<ChangeOrderSortKey>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState<ChangeOrderExportOptions>(DEFAULT_EXPORT_OPTIONS);
  const [exportDialogError, setExportDialogError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ChangeOrderDoc | null>(null);
  const [form, setForm] = useState<ChangeOrderFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [mobilePickerKind, setMobilePickerKind] = useState<MobilePickerKind | null>(null);
  const [mobilePickerSearch, setMobilePickerSearch] = useState('');

  const [detail, setDetail] = useState<ChangeOrderDoc | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [matrixCell, setMatrixCell] = useState<{ unitNo: string; title: string; rows: ChangeOrderDoc[] } | null>(
    null
  );
  const [projectMeta, setProjectMeta] = useState<any | null>(null);
  const [companyMeta, setCompanyMeta] = useState<any | null>(null);
  const [unitPrimaryCustomerByNumber, setUnitPrimaryCustomerByNumber] = useState<Record<string, UnitPrimaryCustomerInfo>>({});

  useEffect(() => {
    setUnits([]);
    setChangeOrders([]);
    setProjectMeta(null);
    setCompanyMeta(null);
    setError(null);
    if (!currentProjectId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubs: Array<() => void> = [];
    unsubs.push(
      onSnapshot(
        query(collection(db, 'projects', currentProjectId, 'units'), orderBy('unitNumberLower', 'asc')),
        (snap) => {
          const rows = snap.docs
            .map((row) => {
              const value = row.data() as any;
              const unitNumber = String(value?.unitNumber || '').trim();
              return {
                id: row.id,
                unitNumber,
                unitNumberLower: String(value?.unitNumberLower || unitNumber.toLowerCase()),
                bookingStatus: String(value?.bookingStatus || '').trim(),
                activeBookingId: String(value?.activeBookingId || '').trim() || null,
                bookingFormDate: String(value?.bookingFormDate || '').trim() || null,
                latestBookingFormDate: String(value?.latestBookingFormDate || '').trim() || null,
                primaryCustomerName: String(value?.primaryCustomerName || '').trim() || null,
                primaryMobile:
                  String(
                    value?.primaryMobile ||
                      value?.primaryCustomerMobile ||
                      value?.mobileNumber ||
                      value?.customerMobile ||
                      ''
                  ).trim() || null,
                customerName: String(value?.customerName || '').trim() || null,
                allotteeName: String(value?.allotteeName || '').trim() || null,
              } as UnitOption;
            })
            .filter((row) => row.unitNumber)
            .sort((a, b) => compareUnitNumbers(a.unitNumber, b.unitNumber));
          setUnits(rows);
          setLoading(false);
        },
        (snapshotError) => {
          console.error('load units failed', snapshotError);
          setError(snapshotError?.message || 'Failed to load units.');
          setLoading(false);
        }
      )
    );
    unsubs.push(
      onSnapshot(
        query(collection(db, 'projects', currentProjectId, 'changeOrders'), orderBy('updatedAt', 'desc')),
        (snap) => {
          const rows = snap.docs.map((row) => {
            const value = row.data() as any;
            return {
              id: row.id,
              coNo: String(value?.coNo || '').trim() || `CO-${row.id.slice(-6).toUpperCase()}`,
              unitId: String(value?.unitId || '').trim(),
              unitNo: String(value?.unitNo || '').trim() || '—',
              customerName: String(value?.customerName || '').trim(),
              title: String(value?.title || '').trim(),
              description: String(value?.description || '').trim(),
              category: String(value?.category || '').trim(),
              agency: String(value?.agency || '').trim(),
              priority: String(value?.priority || 'medium').trim().toLowerCase() as ChangeOrderPriority,
              targetStage: String(value?.targetStage || '').trim(),
              dueDate: value?.dueDate || null,
              status: normalizeChangeOrderStatus(value?.status),
              assignedTo: String(value?.assignedTo || '').trim(),
              costImpact: !!value?.costImpact,
              customerApproval: normalizeChangeOrderCustomerApproval(value?.customerApproval),
              attachments: Array.isArray(value?.attachments)
                ? value.attachments.map((item: any) => ({
                    id: String(item?.id || ''),
                    name: String(item?.name || 'file'),
                    url: String(item?.url || ''),
                    storagePath: String(item?.storagePath || ''),
                    uploadedAt: item?.uploadedAt || null,
                    uploadedBy: String(item?.uploadedBy || ''),
                  }))
                : [],
              createdAt: value?.createdAt || null,
              createdBy: String(value?.createdBy || '').trim(),
              updatedAt: value?.updatedAt || null,
              updatedBy: String(value?.updatedBy || '').trim(),
              statusUpdatedAt: value?.statusUpdatedAt || null,
            } as ChangeOrderDoc;
          });
          setChangeOrders(rows);
        },
        (snapshotError) => {
          console.error('load change orders failed', snapshotError);
          setError(snapshotError?.message || 'Failed to load change orders.');
        }
      )
    );
    return () => {
      unsubs.forEach((unsubscribe) => unsubscribe());
    };
  }, [currentProjectId, db]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentProjectId || !units.length) {
        if (!cancelled) setUnitPrimaryCustomerByNumber({});
        return;
      }
      const missing = units.filter((unit) => {
        const hasInlineMobile = String(unit.primaryMobile || '').trim();
        const hasInlineName = String(unit.primaryCustomerName || unit.customerName || unit.allotteeName || '').trim();
        const bookingId = String(unit.activeBookingId || '').trim();
        return (!hasInlineMobile || !hasInlineName) && !!bookingId;
      });
      if (!missing.length) {
        if (!cancelled) setUnitPrimaryCustomerByNumber({});
        return;
      }
      const entries = await Promise.all(
        missing.map(async (unit) => {
          const bookingId = String(unit.activeBookingId || '').trim();
          if (!bookingId) return [unit.unitNumber, { name: '', mobile: '' }] as const;
          try {
            const customersSnap = await getDocs(
              collection(db, 'projects', currentProjectId, 'units', unit.id, 'bookings', bookingId, 'customers')
            );
            if (customersSnap.empty) return [unit.unitNumber, { name: '', mobile: '' }] as const;
            const customers = customersSnap.docs.map((d) => d.data() as any);
            const primary =
              customers.find((c) => String(c?.role || c?.customerType || '').trim().toLowerCase() === 'primary') ||
              customers.find((c) => c?.isPrimary === true) ||
              customers[0];
            const name = String(
              primary?.fullName ||
                primary?.name ||
                primary?.customerName ||
                [primary?.firstName, primary?.middleName, primary?.lastName].filter(Boolean).join(' ') ||
                ''
            ).trim();
            const mobile = String(
              primary?.mobile || primary?.mobileNo || primary?.phone || primary?.phoneNumber || ''
            ).trim();
            return [unit.unitNumber, { name, mobile }] as const;
          } catch {
            return [unit.unitNumber, { name: '', mobile: '' }] as const;
          }
        })
      );
      if (cancelled) return;
      const next: Record<string, UnitPrimaryCustomerInfo> = {};
      entries.forEach(([unitNumber, info]) => {
        const key = String(unitNumber || '').trim();
        if (key && (String(info.name || '').trim() || String(info.mobile || '').trim())) {
          next[key] = {
            name: String(info.name || '').trim(),
            mobile: String(info.mobile || '').trim(),
          };
        }
      });
      setUnitPrimaryCustomerByNumber(next);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [currentProjectId, db, units]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentProjectId) {
        setProjectMeta(null);
        setCompanyMeta(null);
        return;
      }
      try {
        const projectSnap = await getDoc(doc(db, 'projects', currentProjectId));
        if (!projectSnap.exists()) {
          if (!cancelled) {
            setProjectMeta(null);
            setCompanyMeta(null);
          }
          return;
        }
        const projectData = projectSnap.data() as any;
        if (cancelled) return;
        setProjectMeta(projectData);
        const companyId = String(projectData?.companyId || '').trim();
        if (companyId) {
          const companySnap = await getDoc(doc(db, 'companies', companyId));
          if (!cancelled) setCompanyMeta(companySnap.exists() ? companySnap.data() : null);
        } else {
          setCompanyMeta(null);
        }
      } catch (metaError) {
        if (!cancelled) {
          setProjectMeta(null);
          setCompanyMeta(null);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [currentProjectId, db]);

  useEffect(() => {
    if (!currentProjectId || !detail?.id) {
      setTimeline([]);
      return;
    }
    setTimelineLoading(true);
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'projects', currentProjectId, 'changeOrders', detail.id, 'timeline'),
        orderBy('createdAt', 'desc')
      ),
      (snap) => {
        setTimeline(
          snap.docs.map((row) => {
            const value = row.data() as any;
            return {
              id: row.id,
              type: value?.type || 'updated',
              message: String(value?.message || '').trim(),
              fromStatus: String(value?.fromStatus || '').trim(),
              toStatus: String(value?.toStatus || '').trim(),
              createdAt: value?.createdAt || null,
              createdBy: String(value?.createdBy || '').trim(),
            } as TimelineEvent;
          })
        );
        setTimelineLoading(false);
      },
      () => setTimelineLoading(false)
    );
    return () => unsubscribe();
  }, [currentProjectId, db, detail?.id]);

  const unitById = useMemo(() => {
    const map = new Map<string, UnitOption>();
    for (const row of units) map.set(row.id, row);
    return map;
  }, [units]);

  const unitsWithBookingForm = useMemo(() => {
    return units.filter((row) => {
      return !!(
        String(row.latestBookingFormDate || '').trim() ||
        String(row.bookingFormDate || '').trim() ||
        String(row.activeBookingId || '').trim()
      );
    });
  }, [units]);

  const unitOptionsForForm = useMemo(() => {
    if (!form.unitId) return unitsWithBookingForm;
    const selected = unitById.get(form.unitId);
    if (!selected) return unitsWithBookingForm;
    if (unitsWithBookingForm.some((row) => row.id === selected.id)) return unitsWithBookingForm;
    return [selected, ...unitsWithBookingForm];
  }, [form.unitId, unitById, unitsWithBookingForm]);

  const agencyOptions = useMemo(() => {
    const seen = new Set<string>();
    const rows: string[] = [];
    const sourceRows = buildingFilter
      ? changeOrders.filter((row) => {
          const unitNo = String(row.unitNo || '').trim().toUpperCase();
          const building = unitNo.includes('-') ? unitNo.split('-')[0] : unitNo;
          return building === buildingFilter;
        })
      : changeOrders;
    sourceRows.forEach((row) => {
      const value = String(row.agency || '').trim();
      if (!value || seen.has(value.toLowerCase())) return;
      seen.add(value.toLowerCase());
      rows.push(value);
    });
    rows.sort((a, b) => a.localeCompare(b));
    return rows;
  }, [changeOrders, buildingFilter]);

  const formAgencyOptions = agencyOptions;

  const buildingOptions = useMemo(() => {
    const seen = new Set<string>();
    const rows: string[] = [];
    const add = (unitNo: unknown) => {
      const text = String(unitNo || '').trim().toUpperCase();
      if (!text) return;
      const building = text.includes('-') ? text.split('-')[0] : text;
      const normalized = building.trim();
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      rows.push(normalized);
    };
    changeOrders.forEach((row) => add(row.unitNo));
    return rows.sort((a, b) => a.localeCompare(b));
  }, [changeOrders]);

  const requestTitleOptions = useMemo(() => {
    const seen = new Set<string>();
    const rows: string[] = [];
    const add = (value: unknown) => {
      const text = String(value || '').trim();
      if (!text) return;
      const key = text.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      rows.push(text);
    };
    DEFAULT_CHANGE_ORDER_REQUEST_TITLES.forEach(add);
    changeOrders.forEach((row) => add(row.title));
    return rows.sort((a, b) => a.localeCompare(b));
  }, [changeOrders]);

  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    const rows: string[] = [];
    const add = (value: unknown) => {
      const text = String(value || '').trim();
      if (!text) return;
      const key = text.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      rows.push(text);
    };
    DEFAULT_CHANGE_ORDER_CATEGORIES.forEach(add);
    changeOrders.forEach((row) => add(row.category));
    return rows.sort((a, b) => a.localeCompare(b));
  }, [changeOrders]);

  const targetStageOptions = useMemo(() => {
    const seen = new Set<string>();
    const rows: string[] = [];
    const sourceRows = changeOrders.filter((row) => {
      if (buildingFilter) {
        const unitNo = String(row.unitNo || '').trim().toUpperCase();
        const building = unitNo.includes('-') ? unitNo.split('-')[0] : unitNo;
        if (building !== buildingFilter) return false;
      }
      if (agencyFilter) {
        if (String(row.agency || '').trim() !== agencyFilter) return false;
      }
      return true;
    });
    const add = (value: unknown) => {
      const text = String(value || '').trim();
      if (!text) return;
      const key = text.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      rows.push(text);
    };
    sourceRows.forEach((row) => add(row.targetStage));
    return rows.sort((a, b) => a.localeCompare(b));
  }, [changeOrders, buildingFilter, agencyFilter]);

  useEffect(() => {
    if (agencyFilter && !agencyOptions.includes(agencyFilter)) {
      setAgencyFilter('');
    }
  }, [agencyFilter, agencyOptions]);

  useEffect(() => {
    if (targetStageFilter && !targetStageOptions.includes(targetStageFilter)) {
      setTargetStageFilter('');
    }
  }, [targetStageFilter, targetStageOptions]);

  const summary = useMemo(() => {
    const activeRows = changeOrders.filter((row) => normalizeChangeOrderStatus(row.status) !== 'rejected');
    const openCount = activeRows.filter((row) => isOpenChangeOrderStatus(row.status)).length;
    const pendingApproval = activeRows.filter(
      (row) => row.customerApproval === 'pending' && !isCompletedChangeOrderStatus(row.status)
    ).length;
    const inProgress = activeRows.filter((row) => row.status === 'in_progress').length;
    const now = new Date();
    const overdue = activeRows.filter((row) => {
      if (!isOpenChangeOrderStatus(row.status)) return false;
      const due = asYmd(row.dueDate);
      if (!due) return false;
      return due < asYmd(now);
    }).length;
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const completedThisMonth = activeRows.filter((row) => {
      const updated = asYmd(row.updatedAt);
      return isCompletedChangeOrderStatus(row.status) && updated.startsWith(monthKey);
    }).length;
    return { openCount, pendingApproval, inProgress, overdue, completedThisMonth };
  }, [changeOrders]);

  const matchesSummaryFilter = useCallback((row: ChangeOrderDoc, filter: SummaryFilter) => {
    if (!filter) return true;
    if (filter === 'open') return isOpenChangeOrderStatus(row.status);
    if (filter === 'pending_approval') return row.customerApproval === 'pending';
    if (filter === 'in_progress') return row.status === 'in_progress';
    if (filter === 'overdue') {
      if (!isOpenChangeOrderStatus(row.status)) return false;
      const due = asYmd(row.dueDate);
      if (!due) return false;
      return due < asYmd(new Date());
    }
    if (filter === 'completed_this_month') {
      const now = new Date();
      const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const updated = asYmd(row.updatedAt);
      return isCompletedChangeOrderStatus(row.status) && updated.startsWith(monthKey);
    }
    return true;
  }, []);

  const toggleSummaryFilter = useCallback((filter: SummaryFilter) => {
    setSummaryFilter((current) => (current === filter ? '' : filter));
  }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const next = changeOrders.filter((row) => {
      if (!matchesSummaryFilter(row, summaryFilter)) return false;
      if (agencyFilter && row.agency !== agencyFilter) return false;
      if (targetStageFilter && String(row.targetStage || '').trim() !== targetStageFilter) return false;
      if (buildingFilter) {
        const unitNo = String(row.unitNo || '').trim().toUpperCase();
        const building = unitNo.includes('-') ? unitNo.split('-')[0] : unitNo;
        if (building !== buildingFilter) return false;
      }
      if (!term) return true;
      return [
        row.coNo,
        row.unitNo,
        row.customerName,
        row.title,
        row.description,
        row.category,
        row.agency,
        row.assignedTo,
      ]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
    next.sort((left, right) => {
      if (viewMode === 'agency') {
        const agencyDiff = String(left.agency || '').localeCompare(String(right.agency || ''));
        if (agencyDiff !== 0) return agencyDiff;
      } else {
        const unitDiff = compareUnitNumbers(left.unitNo, right.unitNo);
        if (unitDiff !== 0) return unitDiff;
      }
      const leftDue = asYmd(left.dueDate);
      const rightDue = asYmd(right.dueDate);
      if (leftDue && rightDue && leftDue !== rightDue) return leftDue.localeCompare(rightDue);
      return String(right.coNo || '').localeCompare(String(left.coNo || ''));
    });
    return next;
  }, [agencyFilter, buildingFilter, changeOrders, matchesSummaryFilter, search, summaryFilter, targetStageFilter, viewMode]);

  const visibleRows = useMemo(
    () =>
      filteredRows.filter((row) => {
        if (normalizeChangeOrderStatus(row.status) === 'rejected') return false;
        if (hideCompleted && summaryFilter !== 'completed_this_month' && isCompletedChangeOrderStatus(row.status)) {
          return false;
        }
        return true;
      }),
    [filteredRows, hideCompleted, summaryFilter]
  );

  const handleSort = useCallback((key: ChangeOrderSortKey) => {
    setSortBy((current) => {
      if (current === key) {
        setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
        return current;
      }
      setSortDirection(key === 'updatedAt' ? 'desc' : 'asc');
      return key;
    });
  }, []);

  const sortedTableRows = useMemo(() => {
    const rows = [...visibleRows];
    rows.sort((left, right) => {
      let result = 0;
      if (sortBy === 'unitNo') {
        result = compareUnitNumbers(left.unitNo, right.unitNo);
      } else if (sortBy === 'updatedAt') {
        result = toMillis(left.updatedAt) - toMillis(right.updatedAt);
      } else if (sortBy === 'status') {
        result = statusLabel(left.status).localeCompare(statusLabel(right.status));
      } else {
        result = String(left[sortBy] || '').localeCompare(String(right[sortBy] || ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }
      return sortDirection === 'asc' ? result : -result;
    });
    return rows;
  }, [visibleRows, sortBy, sortDirection]);

  const matrixTitles = useMemo(() => {
    const seen = new Set<string>();
    const titles: string[] = [];
    visibleRows.forEach((row) => {
      const title = String(row.title || '').trim();
      if (!title) return;
      const key = title.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      titles.push(title);
    });
    return titles.sort((a, b) => a.localeCompare(b));
  }, [visibleRows]);

  const matrixTitleCounts = useMemo(() => {
    const counts = new Map<string, number>();
    visibleRows.forEach((row) => {
      const title = String(row.title || '').trim();
      if (!title) return;
      counts.set(title.toLowerCase(), (counts.get(title.toLowerCase()) || 0) + 1);
    });
    return counts;
  }, [visibleRows]);

  const unitPrimaryInfoByUnitNo = useMemo(() => {
    const map = new Map<string, UnitPrimaryCustomerInfo>();
    units.forEach((unit) => {
      const unitNo = String(unit.unitNumber || '').trim();
      if (!unitNo) return;
      const fetched = unitPrimaryCustomerByNumber[unitNo];
      const name = String(
        unit.primaryCustomerName || unit.customerName || unit.allotteeName || fetched?.name || ''
      ).trim();
      const mobile = String(unit.primaryMobile || fetched?.mobile || '').trim();
      if (name || mobile) map.set(unitNo, { name, mobile });
    });
    return map;
  }, [units, unitPrimaryCustomerByNumber]);

  const matrixUnits = useMemo(() => {
    const map = new Map<string, { unitNo: string; customerName: string; customerPhone: string }>();
    visibleRows.forEach((row) => {
      const key = String(row.unitNo || '').trim();
      if (!key) return;
      if (!map.has(key)) {
        const primaryInfo = unitPrimaryInfoByUnitNo.get(key);
        map.set(key, {
          unitNo: key,
          customerName: primaryInfo?.name || row.customerName || '',
          customerPhone: String(primaryInfo?.mobile || '').trim(),
        });
      }
    });
    return Array.from(map.values()).sort((left, right) => compareUnitNumbers(left.unitNo, right.unitNo));
  }, [visibleRows, unitPrimaryInfoByUnitNo]);

  const matrixCellMap = useMemo(() => {
    const map = new Map<string, ChangeOrderDoc[]>();
    visibleRows.forEach((row) => {
      const unitNo = String(row.unitNo || '').trim();
      const title = String(row.title || '').trim();
      if (!unitNo || !title) return;
      const key = `${unitNo}__${title.toLowerCase()}`;
      const bucket = map.get(key) || [];
      bucket.push(row);
      map.set(key, bucket);
    });
    return map;
  }, [visibleRows]);

  const getMatrixDescriptions = (rows: ChangeOrderDoc[]) => {
    const texts = rows
      .map((row) => String(row.description || '').trim())
      .filter(Boolean);
    return texts.length ? texts : ['—'];
  };

  const startCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const startEdit = (row: ChangeOrderDoc) => {
    setEditing(row);
    setForm({
      unitId: row.unitId || '',
      title: row.title || '',
      description: row.description || '',
      category: row.category || '',
      agencies: row.agency ? [row.agency] : [],
      targetStage: row.targetStage || '',
      costImpact: !!row.costImpact,
      customerApproval: row.customerApproval || 'pending',
      status: row.status || 'requested',
    });
    setFormOpen(true);
  };

  const addTimelineEvent = async (
    changeOrderId: string,
    payload: Omit<TimelineEvent, 'id' | 'createdAt'> & { createdBy?: string }
  ) => {
    if (!currentProjectId) return;
    const timelineRef = collection(db, 'projects', currentProjectId, 'changeOrders', changeOrderId, 'timeline');
    await addDoc(timelineRef, {
      ...payload,
      createdAt: serverTimestamp(),
    });
  };

  const saveChangeOrder = async () => {
    if (!currentProjectId || !currentUser?.email || saving) return;
    const selectedUnit = unitById.get(form.unitId);
    if (!selectedUnit) {
      setError('Please select unit.');
      return;
    }
    if (!form.title.trim()) {
      setError('Please enter request title.');
      return;
    }
    if (!form.description.trim()) {
      setError('Please enter change request details.');
      return;
    }
    if (!form.category.trim()) {
      setError('Please enter category.');
      return;
    }
    const normalizedAgencies = Array.from(
      new Set(
        (Array.isArray(form.agencies) ? form.agencies : [])
          .map((value) => String(value || '').trim())
          .filter(Boolean)
      )
    );
    if (!normalizedAgencies.length) {
      const fallbackAgency = String(getDefaultAgencyForCategory(form.category) || '').trim();
      if (fallbackAgency) normalizedAgencies.push(fallbackAgency);
    }
    if (!normalizedAgencies.length) {
      setError('Please select at least one agency.');
      return;
    }
    const customerName =
      selectedUnit.primaryCustomerName || selectedUnit.customerName || selectedUnit.allotteeName || '';
    const payloadBase: Record<string, any> = {
      unitId: selectedUnit.id,
      unitNo: selectedUnit.unitNumber,
      customerName: String(customerName || '').trim(),
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category.trim(),
      priority: (editing?.priority || 'medium') as ChangeOrderPriority,
      targetStage: String(form.targetStage || '').trim(),
      dueDate: editing?.dueDate || null,
      assignedTo: String(editing?.assignedTo || '').trim(),
      costImpact: !!form.costImpact,
      customerApproval: form.customerApproval,
      status: form.status,
      statusUpdatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.email,
    };

    setSaving(true);
    setError(null);
    try {
      if (editing?.id) {
        const changeOrderRef = doc(db, 'projects', currentProjectId, 'changeOrders', editing.id);
        const previousStatus = normalizeChangeOrderStatus(editing.status);
        const existingAgency = String(editing.agency || '').trim().toLowerCase();
        const primaryAgency = normalizedAgencies[0];
        const updatePayload: Record<string, any> = { ...payloadBase, agency: primaryAgency };
        if (isCompletedChangeOrderStatus(form.status) && !isCompletedChangeOrderStatus(previousStatus)) {
          updatePayload.completedAt = serverTimestamp();
          updatePayload.completedBy = currentUser.email;
        }
        await updateDoc(changeOrderRef, updatePayload);
        if (previousStatus !== form.status) {
          await addTimelineEvent(editing.id, {
            type: 'status_change',
            message: `Status changed from ${statusLabel(previousStatus)} to ${statusLabel(form.status)}.`,
            fromStatus: previousStatus,
            toStatus: form.status,
            createdBy: currentUser.email,
          });
        } else {
          await addTimelineEvent(editing.id, {
            type: 'updated',
            message: 'Change order updated.',
            createdBy: currentUser.email,
          });
        }
        const additionalAgencies = normalizedAgencies.slice(1).filter((agency) => agency.toLowerCase() !== existingAgency);
        for (const agency of additionalAgencies) {
          const extraPayload: Record<string, any> = {
            ...payloadBase,
            agency,
            coNo: buildChangeOrderNo(),
            attachments: [],
            createdAt: serverTimestamp(),
            createdBy: currentUser.email,
            splitFromCoNo: editing.coNo || '',
            splitFromChangeOrderId: editing.id,
          };
          if (isCompletedChangeOrderStatus(form.status)) {
            extraPayload.completedAt = serverTimestamp();
            extraPayload.completedBy = currentUser.email;
          }
          const extraRef = await addDoc(collection(db, 'projects', currentProjectId, 'changeOrders'), extraPayload);
          await addTimelineEvent(extraRef.id, {
            type: 'created',
            message: `Change order created for additional agency: ${agency}.`,
            createdBy: currentUser.email,
          });
        }
      } else {
        for (const agency of normalizedAgencies) {
          const createPayload: Record<string, any> = {
            ...payloadBase,
            agency,
            coNo: buildChangeOrderNo(),
            attachments: [],
            createdAt: serverTimestamp(),
            createdBy: currentUser.email,
          };
          if (isCompletedChangeOrderStatus(form.status)) {
            createPayload.completedAt = serverTimestamp();
            createPayload.completedBy = currentUser.email;
          }
          const changeOrderRef = await addDoc(collection(db, 'projects', currentProjectId, 'changeOrders'), {
            ...createPayload,
          });
          await addTimelineEvent(changeOrderRef.id, {
            type: 'created',
            message:
              normalizedAgencies.length > 1
                ? `Change order created for agency: ${agency}.`
                : 'Change order created.',
            createdBy: currentUser.email,
          });
        }
      }
      setFormOpen(false);
      closeMobilePicker();
      setEditing(null);
      setForm(EMPTY_FORM);
    } catch (saveError: any) {
      console.error('save change order failed', saveError);
      setError(saveError?.message || 'Failed to save change order.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!currentProjectId || !currentUser?.email || !detail?.id) return;
    const text = String(comment || '').trim();
    if (!text) return;
    try {
      await addTimelineEvent(detail.id, {
        type: 'comment',
        message: text,
        createdBy: currentUser.email,
      });
      await updateDoc(doc(db, 'projects', currentProjectId, 'changeOrders', detail.id), {
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      setComment('');
    } catch (commentError: any) {
      console.error('add comment failed', commentError);
      setError(commentError?.message || 'Failed to add comment.');
    }
  };

  const handleQuickStatus = async (nextStatus: ChangeOrderStatus) => {
    if (!currentProjectId || !currentUser?.email || !detail?.id) return;
    const prevStatus = normalizeChangeOrderStatus(detail.status);
    if (prevStatus === nextStatus) return;
    try {
      // Shared with the unit page's Change orders card.
      await changeChangeOrderStatus(db, currentProjectId, detail.id, prevStatus, nextStatus, currentUser.email);
      setDetail((previous) => (previous ? { ...previous, status: nextStatus } : previous));
    } catch (statusError: any) {
      console.error('status update failed', statusError);
      setError(statusError?.message || 'Failed to update status.');
    }
  };

  const markChangeOrderCompleted = async (row: ChangeOrderDoc) => {
    if (!isAdmin || !currentProjectId || !currentUser?.email || !row?.id) return;
    const previousStatus = normalizeChangeOrderStatus(row.status);
    if (isCompletedChangeOrderStatus(previousStatus)) return;
    const ok = window.confirm(`Mark ${row.coNo || 'this change order'} as completed?`);
    if (!ok) return;
    setError(null);
    try {
      await updateDoc(doc(db, 'projects', currentProjectId, 'changeOrders', row.id), {
        status: 'done',
        completedAt: serverTimestamp(),
        completedBy: currentUser.email,
        statusUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      await addTimelineEvent(row.id, {
        type: 'status_change',
        message: `Status changed from ${statusLabel(previousStatus)} to ${statusLabel('done')}.`,
        fromStatus: previousStatus,
        toStatus: 'done',
        createdBy: currentUser.email,
      });
      if (detail?.id === row.id) setDetail((previous) => (previous ? { ...previous, status: 'done' } : previous));
      if (hideCompleted) {
        setMatrixCell((previous) => {
          if (!previous) return previous;
          const rows = previous.rows.filter((item) => item.id !== row.id);
          return rows.length ? { ...previous, rows } : null;
        });
      } else {
        setMatrixCell((previous) => {
          if (!previous) return previous;
          return {
            ...previous,
            rows: previous.rows.map((item) => (item.id === row.id ? { ...item, status: 'done' } : item)),
          };
        });
      }
    } catch (completeError: any) {
      console.error('mark change order completed failed', completeError);
      setError(completeError?.message || 'Failed to mark change order completed.');
    }
  };

  const uploadAttachment = async (file: File | null) => {
    if (!file || !currentProjectId || !currentUser?.email || !detail?.id) return;
    setUploadingAttachment(true);
    setError(null);
    try {
      const safeName = file.name.replace(/[^\w.-]/g, '_');
      const storagePath = `change-orders/${currentProjectId}/${detail.id}/${Date.now()}_${safeName}`;
      const uploadRef = ref(storage, storagePath);
      await uploadBytes(uploadRef, file, { contentType: file.type || 'application/octet-stream' });
      const url = await getDownloadURL(uploadRef);
      const nextAttachments = [
        ...(Array.isArray(detail.attachments) ? detail.attachments : []),
        {
          id: `${Date.now()}`,
          name: file.name,
          url,
          storagePath,
          uploadedAt: new Date().toISOString(),
          uploadedBy: currentUser.email,
        } as ChangeOrderAttachment,
      ];
      await updateDoc(doc(db, 'projects', currentProjectId, 'changeOrders', detail.id), {
        attachments: nextAttachments,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email,
      });
      await addTimelineEvent(detail.id, {
        type: 'attachment',
        message: `Attachment uploaded: ${file.name}`,
        createdBy: currentUser.email,
      });
      setDetail((previous) => (previous ? { ...previous, attachments: nextAttachments } : previous));
    } catch (uploadError: any) {
      console.error('upload attachment failed', uploadError);
      setError(uploadError?.message || 'Failed to upload attachment.');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDeleteChangeOrder = async (row: ChangeOrderDoc) => {
    if (!isAdmin || !currentProjectId || !row?.id) return;
    const ok = window.confirm(`Delete ${row.coNo || 'this change order'} permanently?`);
    if (!ok) return;
    setError(null);
    try {
      await deleteDoc(doc(db, 'projects', currentProjectId, 'changeOrders', row.id));
      if (detail?.id === row.id) setDetail(null);
    } catch (deleteError: any) {
      console.error('delete change order failed', deleteError);
      setError(deleteError?.message || 'Failed to delete change order.');
    }
  };

  const openExportDialog = () => {
    setExportOptions({
      search,
      building: buildingFilter,
      agency: agencyFilter,
      targetStage: targetStageFilter,
      summaryFilter,
      viewMode,
      showContact: true,
      hideCompleted,
    });
    setExportDialogError('');
    setExportDialogOpen(true);
  };

  const exportChangeOrdersPdf = async () => {
    if (!currentProjectId || exportPdfBusy) return;
    if (!String(exportOptions.agency || '').trim()) {
      setExportDialogError('Select agency before creating PDF report.');
      return;
    }
    setExportPdfBusy(true);
    setError(null);
    setExportDialogError('');
    try {
      const options = exportOptions;
      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      const generatedAtLabel = now.toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      const safeProject = String(projectMeta?.name || currentProjectId || 'project')
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_-]/g, '');
      const exportRows = changeOrders
        .filter((row) => {
          if (normalizeChangeOrderStatus(row.status) === 'rejected') return false;
          if (
            options.hideCompleted &&
            options.summaryFilter !== 'completed_this_month' &&
            isCompletedChangeOrderStatus(row.status)
          ) {
            return false;
          }
          if (!matchesSummaryFilter(row, options.summaryFilter)) return false;
          if (options.agency && row.agency !== options.agency) return false;
          if (options.targetStage && String(row.targetStage || '').trim() !== options.targetStage) return false;
          if (options.building) {
            const unitNo = String(row.unitNo || '').trim().toUpperCase();
            const building = unitNo.includes('-') ? unitNo.split('-')[0] : unitNo;
            if (building !== options.building) return false;
          }
          const term = options.search.trim().toLowerCase();
          if (!term) return true;
          return [
            row.coNo,
            row.unitNo,
            row.customerName,
            row.title,
            row.description,
            row.category,
            row.agency,
            row.assignedTo,
          ]
            .join(' ')
            .toLowerCase()
            .includes(term);
        })
        .sort((left, right) => {
          if (options.viewMode === 'agency') {
            const agencyDiff = String(left.agency || '').localeCompare(String(right.agency || ''));
            if (agencyDiff !== 0) return agencyDiff;
          } else {
            const unitDiff = compareUnitNumbers(left.unitNo, right.unitNo);
            if (unitDiff !== 0) return unitDiff;
          }
          return String(left.title || '').localeCompare(String(right.title || ''), undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        });
      const exportMatrixTitles = Array.from(
        exportRows.reduce((seen, row) => {
          const title = String(row.title || '').trim();
          if (title) seen.set(title.toLowerCase(), title);
          return seen;
        }, new Map<string, string>())
      )
        .map(([, title]) => title)
        .sort((a, b) => a.localeCompare(b));
      const exportMatrixCellMap = exportRows.reduce((map, row) => {
        const unitNo = String(row.unitNo || '').trim();
        const title = String(row.title || '').trim();
        if (!unitNo || !title) return map;
        const key = `${unitNo}__${title.toLowerCase()}`;
        const bucket = map.get(key) || [];
        bucket.push(row);
        map.set(key, bucket);
        return map;
      }, new Map<string, ChangeOrderDoc[]>());
      const exportMatrixUnits = Array.from(
        exportRows.reduce((map, row) => {
          const unitNo = String(row.unitNo || '').trim();
          if (!unitNo || map.has(unitNo)) return map;
          const primaryInfo = unitPrimaryInfoByUnitNo.get(unitNo);
          map.set(unitNo, {
            unitNo,
            customerName: primaryInfo?.name || row.customerName || '',
            customerPhone: String(primaryInfo?.mobile || '').trim(),
          });
          return map;
        }, new Map<string, { unitNo: string; customerName: string; customerPhone: string }>())
      )
        .map(([, unit]) => unit)
        .sort((left, right) => compareUnitNumbers(left.unitNo, right.unitNo));
      const activeFilters = [
        options.search.trim() ? `Search: ${options.search.trim()}` : '',
        options.building ? `Building: ${options.building}` : '',
        options.agency ? `Agency: ${options.agency}` : '',
        options.targetStage ? `Target Stage: ${options.targetStage}` : '',
        options.summaryFilter ? `Summary: ${summaryFilterLabel(options.summaryFilter)}` : '',
        `Completed: ${options.hideCompleted ? 'Hidden' : 'Shown'}`,
        `View: ${options.viewMode === 'matrix' ? 'Matrix' : options.viewMode === 'agency' ? 'Agency' : 'Unit'}`,
        `Contact: ${options.showContact ? 'Shown' : 'Hidden'}`,
      ].filter(Boolean);

      const bodyHtml =
        options.viewMode === 'matrix'
          ? (() => {
              const headerCells = exportMatrixTitles
                .map(
                  (titleCell) =>
                    `<th style="border:1px solid #bbb;padding:6px 8px;text-align:left;background:#f2f4f7;font-size:10px;">${escHtml(titleCell)}</th>`
                )
                .join('');
              const rowsHtml = exportMatrixUnits
                .map((unit) => {
                  const cells = exportMatrixTitles
                    .map((titleCell) => {
                      const key = `${unit.unitNo}__${titleCell.toLowerCase()}`;
                      const rows = exportMatrixCellMap.get(key) || [];
                      const descriptions = getMatrixDescriptions(rows);
                      const content = descriptions
                        .map(
                          (text, index) =>
                            `<div style="${index > 0 ? 'margin-top:6px;padding-top:6px;border-top:1px dashed #ccc;' : ''}">${escHtml(text)}</div>`
                        )
                        .join('');
                      return `<td style="border:1px solid #bbb;padding:6px 8px;vertical-align:top;font-size:10px;">${content}</td>`;
                    })
                    .join('');
                  const phoneText = formatPhoneReadable(unit.customerPhone || '');
                  const customerInfoHtml = options.showContact
                    ? [
                        unit.customerName
                          ? `<div style="font-weight:400;font-size:9px;line-height:1.2;margin-top:2px;white-space:normal;">${escHtml(unit.customerName)}</div>`
                          : '',
                        phoneText
                          ? `<div style="font-weight:400;font-size:9px;line-height:1.2;margin-top:1px;white-space:normal;">${escHtml(phoneText)}</div>`
                          : '',
                      ].join('')
                    : '';
                  return `<tr><td style="border:1px solid #bbb;padding:6px 8px;font-weight:700;white-space:nowrap;font-size:10px;width:${options.showContact ? '108px' : '78px'};">${escHtml(unit.unitNo)}${customerInfoHtml}</td>${cells}</tr>`;
                })
                .join('');
              return `
                <table style="width:100%;border-collapse:collapse;table-layout:fixed;">
                  <colgroup>
                    <col style="width:${options.showContact ? '108px' : '78px'};" />
                    ${exportMatrixTitles.map(() => '<col />').join('')}
                  </colgroup>
                  <thead>
                    <tr>
                      <th style="border:1px solid #bbb;padding:6px 8px;text-align:left;background:#f2f4f7;font-size:10px;width:${options.showContact ? '108px' : '78px'};white-space:nowrap;">Unit</th>
                      ${headerCells}
                    </tr>
                  </thead>
                  <tbody>
                    ${rowsHtml || `<tr><td colspan="${Math.max(1, exportMatrixTitles.length + 1)}" style="border:1px solid #bbb;padding:10px;text-align:center;">No change orders found.</td></tr>`}
                  </tbody>
                </table>
              `;
            })()
          : (() => {
              const rowsHtml = exportRows
                .map(
                  (row, idx) => {
                    const primaryInfo = unitPrimaryInfoByUnitNo.get(String(row.unitNo || '').trim());
                    const primaryName = String(primaryInfo?.name || row.customerName || '').trim();
                    const phoneText = formatPhoneReadable(primaryInfo?.mobile || '');
                    const unitInfoHtml = options.showContact
                      ? [
                          primaryName
                            ? `<div style="font-size:9px;line-height:1.2;margin-top:2px;">${escHtml(primaryName)}</div>`
                            : '',
                          phoneText
                            ? `<div style="font-size:9px;line-height:1.2;margin-top:1px;">${escHtml(phoneText)}</div>`
                            : '',
                        ].join('')
                      : '';
                    return `
                    <tr>
                      <td style="border:1px solid #bbb;padding:6px 8px;font-size:10px;text-align:center;">${idx + 1}</td>
                      <td style="border:1px solid #bbb;padding:6px 8px;font-size:10px;font-weight:700;">${escHtml(row.coNo)}</td>
                      <td style="border:1px solid #bbb;padding:6px 8px;font-size:10px;">${escHtml(row.unitNo || '—')}${unitInfoHtml}</td>
                      <td style="border:1px solid #bbb;padding:6px 8px;font-size:10px;">${escHtml(primaryName || '—')}</td>
                      <td style="border:1px solid #bbb;padding:6px 8px;font-size:10px;">${escHtml(row.title || '—')}</td>
                      <td style="border:1px solid #bbb;padding:6px 8px;font-size:10px;">${escHtml(row.description || '—')}</td>
                      <td style="border:1px solid #bbb;padding:6px 8px;font-size:10px;">${escHtml(row.category || '—')}</td>
                      <td style="border:1px solid #bbb;padding:6px 8px;font-size:10px;">${escHtml(row.agency || '—')}</td>
                      <td style="border:1px solid #bbb;padding:6px 8px;font-size:10px;">${escHtml(statusLabel(row.status))}</td>
                    </tr>
                  `;
                  }
                )
                .join('');
              return `
                <table style="width:100%;border-collapse:collapse;table-layout:fixed;">
                  <thead>
                    <tr>
                      <th style="border:1px solid #bbb;padding:6px 8px;text-align:center;background:#f2f4f7;font-size:10px;width:30px;">#</th>
                      <th style="border:1px solid #bbb;padding:6px 8px;text-align:left;background:#f2f4f7;font-size:10px;">CO No</th>
                      <th style="border:1px solid #bbb;padding:6px 8px;text-align:left;background:#f2f4f7;font-size:10px;">Unit</th>
                      <th style="border:1px solid #bbb;padding:6px 8px;text-align:left;background:#f2f4f7;font-size:10px;">Customer</th>
                      <th style="border:1px solid #bbb;padding:6px 8px;text-align:left;background:#f2f4f7;font-size:10px;">Title</th>
                      <th style="border:1px solid #bbb;padding:6px 8px;text-align:left;background:#f2f4f7;font-size:10px;">Detailed Request</th>
                      <th style="border:1px solid #bbb;padding:6px 8px;text-align:left;background:#f2f4f7;font-size:10px;">Category</th>
                      <th style="border:1px solid #bbb;padding:6px 8px;text-align:left;background:#f2f4f7;font-size:10px;">Agency</th>
                      <th style="border:1px solid #bbb;padding:6px 8px;text-align:left;background:#f2f4f7;font-size:10px;">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${rowsHtml || '<tr><td colspan="9" style="border:1px solid #bbb;padding:10px;text-align:center;">No change orders found.</td></tr>'}
                  </tbody>
                </table>
              `;
            })();

      const html = `
        <div style="font-family:Arial,Helvetica,sans-serif;color:#111;">
          <div style="margin-bottom:10px;font-size:12px;line-height:1.5;">
            <div><strong>Project:</strong> ${escHtml(String(projectMeta?.name || '—'))}</div>
            <div><strong>Filters:</strong> ${activeFilters.length ? escHtml(activeFilters.join(' | ')) : 'All'}</div>
          </div>
          ${bodyHtml}
        </div>
      `;

      const basePayload = buildPdfPayloadFromTemplate({
          html,
          tokenValues: {},
          visualModel: {
            showTitle: false,
            showRefNo: false,
            showDate: false,
            showPageNumbers: false,
            marginTopCm: 0.2,
            marginBottomCm: 0.2,
            headerHeightCm: 0.625,
            headerTopMarginCm: 0,
            headerFooterSideMarginCm: 0,
            headerVerticalOffsetCm: 0,
            footerHeightCm: 1.1,
            footerOverlayEnabled: false,
            backgroundImageUrl: String(projectMeta?.backgroundImageUrl || ''),
            backgroundOpacity: Number(projectMeta?.backgroundOpacity || 0.12),
            backgroundSize: 'contain',
            backgroundScale: 70,
            backgroundPosX: 50,
            backgroundPosY: 50,
            companyLogoHeightCm: 0,
            projectLogoHeightCm: 1.2,
          },
          companyLogoUrl: '',
          projectLogoUrl: String(projectMeta?.logoUrl || ''),
          companyAddress: String(companyMeta?.address || ''),
          companyMobile: String(companyMeta?.mobile || ''),
          companyEmail: String(companyMeta?.email || ''),
          companyFontFamily: String(companyMeta?.fontFamily || ''),
          companyFontUrl: String(companyMeta?.fontUrl || ''),
          companyFontFormat: String(companyMeta?.fontFormat || ''),
          gujaratiFontFamily: String(companyMeta?.gujaratiFontFamily || ''),
          gujaratiFontUrl: String(companyMeta?.gujaratiFontUrl || ''),
          gujaratiFontFormat: String(companyMeta?.gujaratiFontFormat || ''),
          paperSize: 'A4',
          fileName: `change_orders_${safeProject}_${stamp}`,
        });
      const projectLogoUrl = String(projectMeta?.logoUrl || '').trim();
      const headerHeightMm = 13.53;
      const footerHeightMm = 9;
      const sidePaddingMm = 8;
      const customHeaderTemplate = `
        <div style="position:relative;width:100%;height:${headerHeightMm}mm;box-sizing:border-box;overflow:hidden;">
          ${
            projectLogoUrl
              ? `<img src="${projectLogoUrl}" style="position:absolute;right:${sidePaddingMm}mm;top:0.5mm;height:12mm;object-fit:contain;" />`
              : ''
          }
          <div style="position:absolute;left:${sidePaddingMm}mm;right:${sidePaddingMm}mm;bottom:0;border-bottom:1px solid #bbb;"></div>
        </div>
      `;
      const customFooterTemplate = `
        <div style="position:relative;width:100%;height:${footerHeightMm}mm;box-sizing:border-box;overflow:hidden;">
          <div style="position:absolute;left:${sidePaddingMm}mm;right:${sidePaddingMm}mm;top:0;border-top:1px solid #bbb;"></div>
          <div style="position:absolute;left:${sidePaddingMm}mm;right:${sidePaddingMm}mm;top:0;bottom:0;display:flex;align-items:center;justify-content:center;text-align:center;font-size:9px;line-height:1;color:#555;">
            Generated: ${generatedAtLabel}
          </div>
          <div style="position:absolute;right:${sidePaddingMm}mm;bottom:1.2mm;font-size:10px;line-height:1;color:#444;">Page <span class="pageNumber"></span> of <span class="totalPages"></span></div>
        </div>
      `;
      const payload = {
        ...basePayload,
        headerTemplate: customHeaderTemplate,
        footerTemplate: customFooterTemplate,
        marginTopMm: Math.max(10, headerHeightMm + 7),
        marginBottomMm: Math.max(10, footerHeightMm + 8),
        landscape: true,
      } as Record<string, any>;

      const functions = getFunctions();
      const renderTemplatePdf = httpsCallable(functions, 'renderTemplatePdf');
      const result = await renderTemplatePdf(payload);
      const blob = blobFromRenderTemplateResult(result.data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `change_orders_${safeProject}_${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportDialogOpen(false);
    } catch (exportError: any) {
      console.error('export change orders pdf failed', exportError);
      setError(exportError?.message || 'Failed to export PDF.');
    } finally {
      setExportPdfBusy(false);
    }
  };

  const openMobilePicker = (kind: MobilePickerKind) => {
    setMobilePickerKind(kind);
    setMobilePickerSearch('');
  };

  const closeMobilePicker = () => {
    setMobilePickerKind(null);
    setMobilePickerSearch('');
  };

  const setCategoryValue = (nextCategory: string) => {
    const defaultAgency = getDefaultAgencyForCategory(nextCategory);
    setForm((previous) => ({
      ...previous,
      category: nextCategory,
      agencies:
        previous.agencies.length > 0
          ? previous.agencies
          : defaultAgency
            ? [defaultAgency]
            : previous.agencies,
    }));
  };

  const mobilePickerConfig = useMemo(() => {
    if (!mobilePickerKind) return null;
    if (mobilePickerKind === 'unit') {
      return {
        title: 'Select Unit',
        searchable: true,
        multiple: false,
        freeSolo: false,
        selectedValues: form.unitId ? [form.unitId] : [],
        options: unitOptionsForForm.map((unit) => ({ value: unit.id, label: unit.unitNumber || unit.id })),
      };
    }
    if (mobilePickerKind === 'title') {
      return {
        title: 'Select Request Title',
        searchable: true,
        multiple: false,
        freeSolo: true,
        selectedValues: form.title ? [form.title] : [],
        options: requestTitleOptions.map((value) => ({ value, label: value })),
      };
    }
    if (mobilePickerKind === 'category') {
      return {
        title: 'Select Category',
        searchable: true,
        multiple: false,
        freeSolo: true,
        selectedValues: form.category ? [form.category] : [],
        options: categoryOptions.map((value) => ({ value, label: value })),
      };
    }
    if (mobilePickerKind === 'agency') {
      return {
        title: 'Select Agency',
        searchable: true,
        multiple: true,
        freeSolo: true,
        selectedValues: form.agencies,
        options: formAgencyOptions.map((value) => ({ value, label: value })),
      };
    }
    if (mobilePickerKind === 'targetStage') {
      return {
        title: 'Select Target Stage',
        searchable: true,
        multiple: false,
        freeSolo: true,
        selectedValues: form.targetStage ? [form.targetStage] : [],
        options: targetStageOptions.map((value) => ({ value, label: value })),
      };
    }
    if (mobilePickerKind === 'customerApproval') {
      return {
        title: 'Customer Approval',
        searchable: false,
        multiple: false,
        freeSolo: false,
        selectedValues: [form.customerApproval],
        options: CHANGE_ORDER_CUSTOMER_APPROVAL_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        })),
      };
    }
    if (mobilePickerKind === 'status') {
      return {
        title: 'Status',
        searchable: false,
        multiple: false,
        freeSolo: false,
        selectedValues: [form.status],
        options: CHANGE_ORDER_STATUS_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        })),
      };
    }
    return {
      title: 'Cost Impact',
      searchable: false,
      multiple: false,
      freeSolo: false,
      selectedValues: [form.costImpact ? 'yes' : 'no'],
      options: [
        { value: 'no', label: 'No' },
        { value: 'yes', label: 'Yes' },
      ],
    };
  }, [
    formAgencyOptions,
    categoryOptions,
    form.agencies,
    form.category,
    form.costImpact,
    form.customerApproval,
    form.status,
    form.targetStage,
    form.title,
    form.unitId,
    mobilePickerKind,
    requestTitleOptions,
    targetStageOptions,
    unitOptionsForForm,
  ]);

  const mobilePickerOptions = useMemo(() => {
    if (!mobilePickerConfig) return [];
    const searchText = mobilePickerSearch.trim();
    const normalizedSearch = searchText.toLowerCase();
    const filtered = mobilePickerConfig.searchable && normalizedSearch
      ? mobilePickerConfig.options.filter((option) => option.label.toLowerCase().includes(normalizedSearch))
      : mobilePickerConfig.options;
    const hasExact = mobilePickerConfig.options.some(
      (option) => option.value.toLowerCase() === normalizedSearch || option.label.toLowerCase() === normalizedSearch
    );
    if (mobilePickerConfig.freeSolo && searchText && !hasExact) {
      return [{ value: searchText, label: `Add "${searchText}"` }, ...filtered];
    }
    return filtered;
  }, [mobilePickerConfig, mobilePickerSearch]);

  const selectMobilePickerValue = (option: MobilePickerOption) => {
    if (!mobilePickerKind) return;
    const value = String(option.value || '').trim();
    if (!value) return;
    if (mobilePickerKind === 'unit') {
      const nextCategory = String(form.category || '').trim();
      const defaultAgency = getDefaultAgencyForCategory(nextCategory);
      setForm((previous) => ({
        ...previous,
        unitId: value,
        agencies:
          previous.agencies.length > 0
            ? previous.agencies
            : defaultAgency
              ? [defaultAgency]
              : [],
      }));
      closeMobilePicker();
      return;
    }
    if (mobilePickerKind === 'title') {
      setForm((previous) => ({ ...previous, title: value }));
      closeMobilePicker();
      return;
    }
    if (mobilePickerKind === 'category') {
      setCategoryValue(value);
      closeMobilePicker();
      return;
    }
    if (mobilePickerKind === 'agency') {
      setForm((previous) => {
        const exists = previous.agencies.some((agency) => agency.toLowerCase() === value.toLowerCase());
        return {
          ...previous,
          agencies: exists
            ? previous.agencies.filter((agency) => agency.toLowerCase() !== value.toLowerCase())
            : [...previous.agencies, value],
        };
      });
      setMobilePickerSearch('');
      return;
    }
    if (mobilePickerKind === 'targetStage') {
      setForm((previous) => ({ ...previous, targetStage: value }));
      closeMobilePicker();
      return;
    }
    if (mobilePickerKind === 'customerApproval') {
      setForm((previous) => ({ ...previous, customerApproval: value as ChangeOrderCustomerApproval }));
      closeMobilePicker();
      return;
    }
    if (mobilePickerKind === 'status') {
      setForm((previous) => ({ ...previous, status: value as ChangeOrderStatus }));
      closeMobilePicker();
      return;
    }
    setForm((previous) => ({ ...previous, costImpact: value === 'yes' }));
    closeMobilePicker();
  };

  const mobilePickerField = (
    label: string,
    value: string,
    kind: MobilePickerKind,
    required = false
  ) => (
    <TextField
      label={label}
      value={value}
      required={required}
      fullWidth
      size="medium"
      onClick={() => openMobilePicker(kind)}
      InputProps={{ readOnly: true }}
    />
  );

  const formTitle = editing ? 'Edit Change Order' : 'Add Change Order';
  const formFields = (
    <Grid container spacing={1.25}>
      <Grid size={{ xs: 12, md: 5 }}>
        {isMobile ? (
          mobilePickerField(
            'Unit',
            unitOptionsForForm.find((row) => row.id === form.unitId)?.unitNumber || '',
            'unit',
            true
          )
        ) : (
          <Autocomplete
            options={unitOptionsForForm}
            value={unitOptionsForForm.find((row) => row.id === form.unitId) || null}
            onChange={(_, next) => {
              const nextCategory = String(form.category || '').trim();
              const defaultAgency = getDefaultAgencyForCategory(nextCategory);
              setForm((previous) => ({
                ...previous,
                unitId: next?.id || '',
                agencies:
                  previous.agencies.length > 0
                    ? previous.agencies
                    : defaultAgency
                      ? [defaultAgency]
                      : [],
              }));
            }}
            getOptionLabel={(option) => option.unitNumber || ''}
            renderInput={(params) => <TextField {...params} label="Unit" size="small" required />}
          />
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 7 }}>
        {isMobile ? (
          mobilePickerField('Request Title', form.title, 'title', true)
        ) : (
          <Autocomplete
            freeSolo
            options={requestTitleOptions}
            value={form.title}
            onInputChange={(_, next) => setForm((previous) => ({ ...previous, title: next }))}
            onChange={(_, next) => setForm((previous) => ({ ...previous, title: String(next || '') }))}
            renderInput={(params) => <TextField {...params} size="small" label="Request Title" required fullWidth />}
          />
        )}
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          size={isMobile ? 'medium' : 'small'}
          label="Detailed Change Request"
          value={form.description}
          onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))}
          required
          multiline
          minRows={3}
          fullWidth
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        {isMobile ? (
          mobilePickerField('Category', form.category, 'category', true)
        ) : (
          <Autocomplete
            freeSolo
            options={categoryOptions}
            value={form.category}
            onInputChange={(_, next) => setCategoryValue(next)}
            onChange={(_, next) => setCategoryValue(String(next || ''))}
            renderInput={(params) => <TextField {...params} size="small" label="Category" required fullWidth />}
          />
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        {isMobile ? (
          mobilePickerField('Agency', form.agencies.join(', '), 'agency', true)
        ) : (
          <Autocomplete
            multiple
            freeSolo
            options={formAgencyOptions}
            value={form.agencies}
            onChange={(_, next) =>
              setForm((previous) => ({
                ...previous,
                agencies: Array.from(
                  new Set((Array.isArray(next) ? next : []).map((value) => String(value || '').trim()).filter(Boolean))
                ),
              }))
            }
            renderInput={(params) => <TextField {...params} label="Agency" size="small" required fullWidth />}
          />
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        {isMobile ? (
          mobilePickerField('Target Stage', form.targetStage, 'targetStage')
        ) : (
          <Autocomplete
            freeSolo
            options={targetStageOptions}
            value={form.targetStage}
            onInputChange={(_, next) => setForm((previous) => ({ ...previous, targetStage: next }))}
            onChange={(_, next) =>
              setForm((previous) => ({ ...previous, targetStage: String(next || '') }))
            }
            renderInput={(params) => <TextField {...params} size="small" label="Target Stage" fullWidth />}
          />
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        {isMobile ? (
          mobilePickerField(
            'Customer Approval',
            CHANGE_ORDER_CUSTOMER_APPROVAL_OPTIONS.find((option) => option.value === form.customerApproval)?.label || '',
            'customerApproval'
          )
        ) : (
          <FormControl size="small" fullWidth>
            <InputLabel>Customer Approval</InputLabel>
            <Select
              label="Customer Approval"
              value={form.customerApproval}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  customerApproval: event.target.value as ChangeOrderCustomerApproval,
                }))
              }
            >
              {CHANGE_ORDER_CUSTOMER_APPROVAL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        {isMobile ? (
          mobilePickerField(
            'Status',
            CHANGE_ORDER_STATUS_OPTIONS.find((option) => option.value === form.status)?.label || '',
            'status'
          )
        ) : (
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={form.status}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, status: event.target.value as ChangeOrderStatus }))
              }
            >
              {CHANGE_ORDER_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        {isMobile ? (
          mobilePickerField('Cost Impact', form.costImpact ? 'Yes' : 'No', 'costImpact')
        ) : (
          <FormControl size="small" fullWidth>
            <InputLabel>Cost Impact</InputLabel>
            <Select
              label="Cost Impact"
              value={form.costImpact ? 'yes' : 'no'}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, costImpact: event.target.value === 'yes' }))
              }
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </Select>
          </FormControl>
        )}
      </Grid>
    </Grid>
  );

  const formFooterActions = (
    <>
      <Button
        onClick={() => {
          setFormOpen(false);
          closeMobilePicker();
        }}
        disabled={saving}
      >
        Cancel
      </Button>
      <Button onClick={saveChangeOrder} variant="contained" disabled={saving}>
        {editing ? 'Save Changes' : 'Create Change Order'}
      </Button>
    </>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 } }}>
      <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.75 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1}
          sx={{ mb: 1.25 }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Change Orders
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={openExportDialog}
              disabled={exportPdfBusy}
            >
              {exportPdfBusy ? 'Exporting…' : 'Export PDF'}
            </Button>
            <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={startCreate}>
              Add Change Order
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 1.25 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} sx={{ mb: 1.25 }}>
          <TextField
            size="small"
            label="Search unit/customer/request"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: { xs: '100%', lg: 300 }, flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Building</InputLabel>
            <Select
              label="Building"
              value={buildingFilter}
              onChange={(event) => setBuildingFilter(String(event.target.value || ''))}
            >
              <MenuItem value="">All Buildings</MenuItem>
              {buildingOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Agency</InputLabel>
            <Select
              label="Agency"
              value={agencyFilter}
              onChange={(event) => setAgencyFilter(String(event.target.value || ''))}
            >
              <MenuItem value="">All Agencies</MenuItem>
              {agencyOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Target Stage</InputLabel>
            <Select
              label="Target Stage"
              value={targetStageFilter}
              onChange={(event) => setTargetStageFilter(String(event.target.value || ''))}
            >
              <MenuItem value="">All Stages</MenuItem>
              {targetStageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, next) => {
              if (next) setViewMode(next);
            }}
          >
            <ToggleButton value="agency">Agency View</ToggleButton>
            <ToggleButton value="unit">Unit View</ToggleButton>
            <ToggleButton value="matrix">Matrix View</ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            sx={{ ml: { xs: 0, lg: 0.5 }, whiteSpace: 'nowrap' }}
            control={
              <Checkbox
                size="small"
                checked={hideCompleted}
                onChange={(event) => setHideCompleted(event.target.checked)}
              />
            }
            label="Hide completed"
          />
        </Stack>

        <Box
          sx={{
            mb: 1.25,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(5, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          <Box>
            <Card
              variant="outlined"
              role="button"
              tabIndex={0}
              onClick={() => toggleSummaryFilter('open')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') toggleSummaryFilter('open');
              }}
              sx={{
                cursor: 'pointer',
                borderColor: summaryFilter === 'open' ? 'primary.main' : 'divider',
                bgcolor: summaryFilter === 'open' ? 'action.selected' : 'background.paper',
              }}
            >
              <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Typography variant="caption" color="text.secondary">
                  Open COs
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {summary.openCount}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card
              variant="outlined"
              role="button"
              tabIndex={0}
              onClick={() => toggleSummaryFilter('pending_approval')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') toggleSummaryFilter('pending_approval');
              }}
              sx={{
                cursor: 'pointer',
                borderColor: summaryFilter === 'pending_approval' ? 'primary.main' : 'divider',
                bgcolor: summaryFilter === 'pending_approval' ? 'action.selected' : 'background.paper',
              }}
            >
              <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Typography variant="caption" color="text.secondary">
                  Pending Approval
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {summary.pendingApproval}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card
              variant="outlined"
              role="button"
              tabIndex={0}
              onClick={() => toggleSummaryFilter('in_progress')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') toggleSummaryFilter('in_progress');
              }}
              sx={{
                cursor: 'pointer',
                borderColor: summaryFilter === 'in_progress' ? 'primary.main' : 'divider',
                bgcolor: summaryFilter === 'in_progress' ? 'action.selected' : 'background.paper',
              }}
            >
              <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Typography variant="caption" color="text.secondary">
                  In Progress
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {summary.inProgress}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card
              variant="outlined"
              role="button"
              tabIndex={0}
              onClick={() => toggleSummaryFilter('overdue')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') toggleSummaryFilter('overdue');
              }}
              sx={{
                cursor: 'pointer',
                borderColor: summaryFilter === 'overdue' ? 'primary.main' : 'divider',
                bgcolor: summaryFilter === 'overdue' ? 'action.selected' : 'background.paper',
              }}
            >
              <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Typography variant="caption" color="text.secondary">
                  Overdue
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {summary.overdue}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 2', md: 'span 1' } }}>
            <Card
              variant="outlined"
              role="button"
              tabIndex={0}
              onClick={() => toggleSummaryFilter('completed_this_month')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') toggleSummaryFilter('completed_this_month');
              }}
              sx={{
                cursor: 'pointer',
                borderColor: summaryFilter === 'completed_this_month' ? 'primary.main' : 'divider',
                bgcolor: summaryFilter === 'completed_this_month' ? 'action.selected' : 'background.paper',
              }}
            >
              <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Typography variant="caption" color="text.secondary">
                  Completed (This Month)
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {summary.completedThisMonth}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {viewMode === 'matrix' ? (
          <TableContainer
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: '62vh', overflowX: 'auto' }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      minWidth: 120,
                      fontWeight: 700,
                      position: { xs: 'sticky', md: 'static' },
                      left: { xs: 0, md: 'auto' },
                      zIndex: { xs: 4, md: 'auto' },
                      bgcolor: 'background.paper',
                    }}
                  >
                    Unit
                  </TableCell>
                  {matrixTitles.map((title) => (
                    <TableCell key={title} sx={{ minWidth: 180, fontWeight: 700 }}>
                      {title} ({matrixTitleCounts.get(title.toLowerCase()) || 0})
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(2, matrixTitles.length + 1)} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : matrixUnits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(2, matrixTitles.length + 1)} align="center">
                      No change orders found.
                    </TableCell>
                  </TableRow>
                ) : (
                  matrixUnits.map((unit) => (
                    <TableRow key={unit.unitNo} hover>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          position: { xs: 'sticky', md: 'static' },
                          left: { xs: 0, md: 'auto' },
                          zIndex: { xs: 3, md: 'auto' },
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {unit.unitNo}
                          </Typography>
                        </Box>
                      </TableCell>
                      {matrixTitles.map((title) => {
                        const key = `${unit.unitNo}__${title.toLowerCase()}`;
                        const rows = matrixCellMap.get(key) || [];
                        return (
                          <TableCell key={`${unit.unitNo}-${title}`} sx={{ verticalAlign: 'middle' }}>
                            {rows.length ? (
                              <Stack
                                spacing={0.75}
                                sx={{ width: '100%' }}
                              >
                                {rows.map((row) => {
                                  const completed = isCompletedChangeOrderStatus(row.status);
                                  return (
                                    <Paper
                                      key={row.id}
                                      variant="outlined"
                                      onClick={() => setMatrixCell({ unitNo: unit.unitNo, title, rows })}
                                      sx={{
                                        px: 1,
                                        py: 0.75,
                                        cursor: 'pointer',
                                        borderColor: completed ? 'success.light' : 'primary.light',
                                        bgcolor: completed ? 'rgba(46, 125, 50, 0.06)' : 'background.paper',
                                      }}
                                    >
                                      <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                        <Tooltip title={row.description || '—'}>
                                          <Typography
                                            variant="body2"
                                            color="text.primary"
                                            sx={{
                                              flex: 1,
                                              whiteSpace: 'normal',
                                              wordBreak: 'break-word',
                                              textAlign: 'left',
                                            }}
                                          >
                                            {row.description || '—'}
                                          </Typography>
                                        </Tooltip>
                                        {isAdmin && !completed ? (
                                          <Tooltip title="Mark completed">
                                            <IconButton
                                              size="small"
                                              color="success"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                void markChangeOrderCompleted(row);
                                              }}
                                              sx={{ mt: -0.5 }}
                                            >
                                              <CheckCircleOutlineIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : null}
                                      </Stack>
                                    </Paper>
                                  );
                                })}
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    ['coNo', 'CO No'],
                    ['unitNo', 'Unit'],
                    ['customerName', 'Customer'],
                    ['agency', 'Agency'],
                    ['title', 'Title'],
                    ['status', 'Status'],
                    ['updatedAt', 'Updated'],
                  ].map(([key, label]) => (
                    <TableCell key={key}>
                      <TableSortLabel
                        active={sortBy === key}
                        direction={sortBy === key ? sortDirection : 'asc'}
                        onClick={() => handleSort(key as ChangeOrderSortKey)}
                      >
                        {label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : sortedTableRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No change orders found.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedTableRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => setDetail(row)}
                      sx={{ cursor: 'pointer', '& > td': { verticalAlign: 'top' } }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>{row.coNo}</TableCell>
                      <TableCell>{row.unitNo || '—'}</TableCell>
                      <TableCell>{row.customerName || '—'}</TableCell>
                      <TableCell>{row.agency || '—'}</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{row.title || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.category || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{statusLabel(row.status)}</TableCell>
                      <TableCell>{formatDateTime(row.updatedAt)}</TableCell>
                      <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                        {isAdmin && !isCompletedChangeOrderStatus(row.status) ? (
                          <Tooltip title="Mark completed">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => void markChangeOrderCompleted(row)}
                            >
                              <CheckCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {isAdmin ? (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => void handleDeleteChangeOrder(row)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => startEdit(row)}>
                            <SaveOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Details">
                          <IconButton size="small" onClick={() => setDetail(row)}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Change Orders PDF</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                size="small"
                label="Search unit/customer/request"
                fullWidth
                value={exportOptions.search}
                onChange={(event) =>
                  setExportOptions((previous) => ({ ...previous, search: event.target.value }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Building</InputLabel>
                <Select
                  label="Building"
                  value={exportOptions.building}
                  onChange={(event) =>
                    setExportOptions((previous) => ({ ...previous, building: String(event.target.value || '') }))
                  }
                >
                  <MenuItem value="">All Buildings</MenuItem>
                  {buildingOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {exportDialogError ? (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">{exportDialogError}</Alert>
              </Grid>
            ) : null}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl size="small" fullWidth required error={!!exportDialogError && !exportOptions.agency}>
                <InputLabel>Agency</InputLabel>
                <Select
                  label="Agency"
                  value={exportOptions.agency}
                  onChange={(event) =>
                    setExportOptions((previous) => {
                      setExportDialogError('');
                      return { ...previous, agency: String(event.target.value || '') };
                    })
                  }
                >
                  <MenuItem value="">All Agencies</MenuItem>
                  {agencyOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Target Stage</InputLabel>
                <Select
                  label="Target Stage"
                  value={exportOptions.targetStage}
                  onChange={(event) =>
                    setExportOptions((previous) => ({ ...previous, targetStage: String(event.target.value || '') }))
                  }
                >
                  <MenuItem value="">All Stages</MenuItem>
                  {targetStageOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Summary Filter</InputLabel>
                <Select
                  label="Summary Filter"
                  value={exportOptions.summaryFilter}
                  onChange={(event) =>
                    setExportOptions((previous) => ({
                      ...previous,
                      summaryFilter: event.target.value as SummaryFilter,
                    }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="pending_approval">Pending Approval</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="completed_this_month">Completed This Month</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>View</InputLabel>
                <Select
                  label="View"
                  value={exportOptions.viewMode}
                  onChange={(event) =>
                    setExportOptions((previous) => ({
                      ...previous,
                      viewMode: event.target.value as ChangeOrderViewMode,
                    }))
                  }
                >
                  <MenuItem value="matrix">Matrix View</MenuItem>
                  <MenuItem value="agency">Agency View</MenuItem>
                  <MenuItem value="unit">Unit View</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportOptions.showContact}
                    onChange={(event) =>
                      setExportOptions((previous) => ({ ...previous, showContact: event.target.checked }))
                    }
                  />
                }
                label="Show customer name & mobile under unit"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportOptions.hideCompleted}
                    onChange={(event) =>
                      setExportOptions((previous) => ({ ...previous, hideCompleted: event.target.checked }))
                    }
                  />
                }
                label="Hide completed change orders"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={exportChangeOrdersPdf} disabled={exportPdfBusy || !exportOptions.agency}>
            {exportPdfBusy ? 'Exporting…' : 'Create PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!matrixCell} onClose={() => setMatrixCell(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {matrixCell ? `${matrixCell.unitNo} • ${matrixCell.title}` : 'Change Orders'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            {(matrixCell?.rows || []).map((row) => (
              <Paper
                key={row.id}
                variant="outlined"
                sx={{ p: 1.25, cursor: 'pointer' }}
                onClick={() => {
                  setMatrixCell(null);
                  setDetail(row);
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>{row.coNo}</Typography>
                    <Typography variant="body2">{row.customerName || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.category || '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    <Chip size="small" label={statusLabel(row.status)} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {formatDateTime(row.updatedAt)}
                    </Typography>
                    {isAdmin && !isCompletedChangeOrderStatus(row.status) ? (
                      <Button
                        size="small"
                        color="success"
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={(event) => {
                          event.stopPropagation();
                          void markChangeOrderCompleted(row);
                        }}
                        sx={{ mt: 0.75 }}
                      >
                        Mark Completed
                      </Button>
                    ) : null}
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatrixCell(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={formOpen}
          onClose={
            saving
              ? () => undefined
              : () => {
                  setFormOpen(false);
                  closeMobilePicker();
                }
          }
          onOpen={() => undefined}
          disableSwipeToOpen
          swipeAreaWidth={0}
          PaperProps={{
            sx: {
              bottom: 0,
              marginBottom: 0,
              height: '84dvh',
              maxHeight: 'calc(100dvh - 16px)',
              borderRadius: '16px 16px 0 0',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          }}
          sx={{
            '& .MuiDrawer-paperAnchorBottom': {
              bottom: '0 !important',
              marginBottom: '0 !important',
              height: '84dvh !important',
              maxHeight: 'calc(100dvh - 16px) !important',
            },
          }}
        >
          <Box sx={{ pt: 1.25, pb: 0.5, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#ddd' }} />
          </Box>
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {formTitle}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
            {formFields}
          </Box>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {formFooterActions}
            </Stack>
          </Box>
        </SwipeableDrawer>
      ) : (
        <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{formTitle}</DialogTitle>
          <DialogContent dividers>{formFields}</DialogContent>
          <DialogActions>{formFooterActions}</DialogActions>
        </Dialog>
      )}

      <SwipeableDrawer
        anchor="bottom"
        open={isMobile && !!mobilePickerKind && !!mobilePickerConfig}
        onClose={closeMobilePicker}
        onOpen={() => undefined}
        disableSwipeToOpen
        swipeAreaWidth={0}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            zIndex: (drawerTheme) => drawerTheme.zIndex.modal + 4,
            bottom: 0,
            marginBottom: 0,
            maxHeight: '78dvh',
            borderRadius: '16px 16px 0 0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        sx={{
          '& .MuiDrawer-paperAnchorBottom': {
            bottom: '0 !important',
            marginBottom: '0 !important',
            maxHeight: '78dvh !important',
          },
        }}
      >
        {mobilePickerConfig && (
          <>
            <Box sx={{ pt: 1.25, pb: 0.5, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#ddd' }} />
            </Box>
            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {mobilePickerConfig.title}
              </Typography>
              {mobilePickerConfig.searchable && (
                <TextField
                  autoFocus
                  fullWidth
                  size="medium"
                  value={mobilePickerSearch}
                  onChange={(event) => setMobilePickerSearch(event.target.value)}
                  placeholder="Search or type new value"
                  sx={{ mt: 1.25 }}
                />
              )}
            </Box>
            <List
              sx={{
                flex: 1,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                py: 0.5,
              }}
            >
              {mobilePickerOptions.length === 0 ? (
                <Box sx={{ px: 2, py: 3, color: 'text.secondary' }}>No options found.</Box>
              ) : (
                mobilePickerOptions.map((option) => {
                  const checked = mobilePickerConfig.selectedValues.some(
                    (value) => value.toLowerCase() === option.value.toLowerCase()
                  );
                  return (
                    <ListItemButton key={`${option.value}-${option.label}`} onClick={() => selectMobilePickerValue(option)}>
                      {mobilePickerConfig.multiple && <Checkbox edge="start" checked={checked} tabIndex={-1} />}
                      <ListItemText
                        primary={option.label}
                        primaryTypographyProps={{
                          sx: { fontWeight: checked ? 700 : 400, overflowWrap: 'anywhere' },
                        }}
                      />
                    </ListItemButton>
                  );
                })
              )}
            </List>
            <Box
              sx={{
                px: 2,
                py: 1.25,
                pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Button onClick={closeMobilePicker}>Close</Button>
                {mobilePickerConfig.multiple && (
                  <Button variant="contained" onClick={closeMobilePicker}>
                    Done
                  </Button>
                )}
              </Stack>
            </Box>
          </>
        )}
      </SwipeableDrawer>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {detail?.coNo || 'Change Order'}
          {detail?.unitNo ? (
            <Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500 }}>
              • {detail.unitNo}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent dividers>
          {!detail ? null : (
            <Stack spacing={1.25}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Chip label={statusLabel(detail.status)} color={detail.status === 'in_progress' ? 'warning' : 'default'} />
                <Chip label={String(detail.priority || '').toUpperCase()} color={priorityColor(detail.priority)} />
                <Typography variant="body2" color="text.secondary">
                  Agency: {detail.agency || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Due: {asYmd(detail.dueDate) || '—'}
                </Typography>
              </Stack>

              <Typography sx={{ fontWeight: 700 }}>{detail.title || '—'}</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{detail.description || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">
                Customer: {detail.customerName || '—'} • Target Stage: {detail.targetStage || '—'} • Assigned To:{' '}
                {detail.assignedTo || '—'}
              </Typography>

              <Divider />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Quick Status</InputLabel>
                  <Select
                    label="Quick Status"
                    value={detail.status}
                    onChange={(event) => handleQuickStatus(event.target.value as ChangeOrderStatus)}
                  >
                    {CHANGE_ORDER_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button size="small" variant="outlined" onClick={() => startEdit(detail)}>
                  Edit Details
                </Button>
                {isAdmin && !isCompletedChangeOrderStatus(detail.status) ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => void markChangeOrderCompleted(detail)}
                  >
                    Mark Completed
                  </Button>
                ) : null}
                {isAdmin ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => void handleDeleteChangeOrder(detail)}
                  >
                    Delete
                  </Button>
                ) : null}
              </Stack>

              <Divider />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                    Timeline
                  </Typography>
                  <Stack spacing={0.75} sx={{ maxHeight: 220, overflowY: 'auto', pr: 0.5 }}>
                    {timelineLoading ? (
                      <Typography variant="body2" color="text.secondary">
                        Loading timeline...
                      </Typography>
                    ) : timeline.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No timeline events yet.
                      </Typography>
                    ) : (
                      timeline.map((row) => (
                        <Card key={row.id} variant="outlined">
                          <CardContent sx={{ py: 0.75, '&:last-child': { pb: 0.75 } }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {row.message || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(row.createdAt)} • {row.createdBy || '—'}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </Stack>
                </Box>
                <Box sx={{ width: { xs: '100%', md: 300 } }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                    Add Comment
                  </Typography>
                  <TextField
                    size="small"
                    multiline
                    minRows={3}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    fullWidth
                  />
                  <Button sx={{ mt: 0.75 }} variant="contained" onClick={handleAddComment} disabled={!comment.trim()}>
                    Post Comment
                  </Button>
                </Box>
              </Stack>

              <Divider />

              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">Attachments</Typography>
                  <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    startIcon={<AttachFileOutlinedIcon />}
                    disabled={uploadingAttachment}
                  >
                    Upload
                    <input
                      hidden
                      type="file"
                      onChange={(event) => {
                        const file = event.target.files?.[0] || null;
                        void uploadAttachment(file);
                        event.currentTarget.value = '';
                      }}
                    />
                  </Button>
                </Stack>
                {!detail.attachments.length ? (
                  <Typography variant="body2" color="text.secondary">
                    No attachments.
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {detail.attachments.map((row) => (
                      <Button
                        key={row.id}
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="text"
                        sx={{ justifyContent: 'flex-start' }}
                      >
                        {row.name}
                      </Button>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
